import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Link2 } from "lucide-react";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { buildPppcPolicyActions } from "@/components/actions";
import { AppIcon } from "@/components/applications/AppIcon";
import { InteractiveTitle } from "@/components/ui/interactive-title";
import { PppcPolicySnapshot, PppcServiceKey } from "@/types/pppc";

interface PppcPolicyRowProps {
  policy: PppcPolicySnapshot;
  linkedToCatalog: boolean;
  iconUrl?: string | null;
  onView: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const formatDate = (iso?: string) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "—";
  }
};

export const PppcPolicyRow = ({
  policy,
  linkedToCatalog,
  iconUrl,
  onView,
  onEdit,
  onDuplicate,
  onDelete,
}: PppcPolicyRowProps) => {
  const keys = Object.keys(policy.choices) as PppcServiceKey[];
  const allow = keys.filter((k) => policy.choices[k] === "allow").length;
  const deny = keys.filter((k) => policy.choices[k] === "deny").length;

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <AppIcon
            iconSrc={iconUrl ?? null}
            appName={policy.appName || policy.policyName || "PPPC Policy"}
            className="w-6 h-6"
          />
          <InteractiveTitle
            onClick={onView}
            className="font-medium max-w-[200px]"
          >
            {policy.appName || policy.policyName || "PPPC Policy"}
          </InteractiveTitle>
        </div>
      </TableCell>
      <TableCell>
        <span className="text-xs font-mono text-muted-foreground truncate max-w-[260px] inline-block align-middle">
          {policy.identifier || "—"}
        </span>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="text-xs">
          {policy.identifierType === "path" ? "Path" : "Bundle ID"}
        </Badge>
      </TableCell>
      <TableCell>{keys.length}</TableCell>
      <TableCell>
        <div className="flex gap-1">
          {allow > 0 && (
            <Badge variant="outline" className="border-success text-success text-xs">
              {allow}A
            </Badge>
          )}
          {deny > 0 && (
            <Badge variant="outline" className="border-destructive text-destructive text-xs">
              {deny}D
            </Badge>
          )}
          {keys.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
        </div>
      </TableCell>
      <TableCell>
        {linkedToCatalog ? (
          <Badge variant="secondary" className="text-xs gap-1">
            <Link2 className="h-3 w-3" /> Linked
          </Badge>
        ) : (
          <Badge variant="outline" className="text-xs">External</Badge>
        )}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {formatDate(policy.updatedAt)}
      </TableCell>
      <TableCell className="text-right">
        <ActionsDropdown
          align="end"
          actions={buildPppcPolicyActions({
            surface: "list",
            onView,
            onEdit,
            onDuplicate,
            onDelete,
          })}
        />
      </TableCell>
    </TableRow>
  );
};
